import React, { useState, useEffect } from 'react';
import './FilterSelect.css';
import { isEqual } from 'lodash';

const conditionOptions = [
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater or equal to' },
  { value: 'eq', label: 'Equal to' },
  { value: 'lte', label: 'Less or equal to' },
  { value: 'lt', label: 'Less than' },
];

interface FilterConditionalProps {
  name: string;
  label: string;
  value?: string;
  onChange?: (name: string, value: any) => void;
}

const FilterConditionalComponent: React.FC<FilterConditionalProps> = ({
  name,
  label,
  value,
  onChange,
}) => {
  const [condition, setCondition] = useState<string>('eq');
  const [inputValue, setInputValue] = useState<string>('');

  useEffect(() => {
    if (value) {
      // Parse value if it matches field.condition(value)
      const match = value.match(new RegExp(`^${name}\\.(\\w+)\\((.+)\\)$`));
      if (match) {
        setCondition(match[1]);
        setInputValue(match[2]);
      } else {
        setInputValue(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounce effect for input and condition changes
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue) {
        // Always output field.condition(value) and never nest
        onChange && onChange(name, `${name}.${condition}(${inputValue})`);
      }
    }, 1100);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, inputValue]);

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCondition(e.target.value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleClear = () => {
    setInputValue('');
    onChange && onChange(name, '');
  };

  return (
    <div>
      <label className="slicer-label">{label}</label>
      <div className="filter-text-container filter-text-conditional">
        <select
          className="filter-condition-select"
          value={condition}
          onChange={handleConditionChange}
        >
          {conditionOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="filter-text-input  filter-text-input-conditional"
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Enter value"
        />
        {inputValue && (
          <button
            type="button"
            className="filter-text-clear"
            onClick={handleClear}
            aria-label="Clear"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

const arePropsEqual = (
  prevState: FilterConditionalProps,
  nextState: FilterConditionalProps
): boolean =>
  prevState.name === nextState.name &&
  prevState.label === nextState.label &&
  prevState.value === nextState.value &&
  isEqual(prevState.onChange, nextState.onChange);

export const FilterConditional = React.memo(
  FilterConditionalComponent,
  arePropsEqual
);
